import css from "./PageEditor.module.css";

import { GameVer, Lang, useRealm, useT } from "~/util/Lang";
import { Button } from "../ui/Button";
import clsx from "clsx";
import { Heading3, Heading4 } from "../ui/Heading";
import { ReactNode, useEffect, useRef, useState } from "react";
import { AuthUser } from "~/auth.server";
import { callApi } from "~/util/Loaders";
import {
  deletePageApi,
  editPageApi,
  previewPageApi,
} from "~/routes/__site/api/mdPages.pages";
import { Root } from "node_modules/remark-parse/lib";
import { MDDat } from "~/util/MarkdownRenderer";
import { MdContent } from "./MdContent";
import { CheckBox } from "../form/CheckBox";
import { Params, useNavigate, useParams } from "react-router";
import { uploadImageApi } from "~/routes/__site/api/uploadImage";
import { TitleInput } from "./editor/TitleInput";
import { GverSelector } from "./editor/GverSelector";
import { ImageUploader } from "./editor/ImageUploader";
import { Logo } from "./Logo";
import { Breadcrumb } from "../ui/Breadcrumb";
import { BookIcon } from "./icons/Book";
import { FloppyIcon } from "./icons/Floppy";
import { generateUrl } from "~/util/wiki";
import { PageIcon } from "./icons/Page";
import { CloseIcon } from "./icons/Close";

type Page =
  | {
      id: number;
      game: string[] | null;
      revision: {
        id: number;
        createdAt: Date;
        titleEn: string | null;
        contentEn: string | null;
        titleRu: string | null;
        contentRu: string | null;
        images: number[] | null;
      };
      draft?: {
        public: boolean;
      };
      images?: {
        id: number;
        path: string;
      }[];
      content: { en: Markdown; ru: Markdown };
    }
  | undefined;

type MdPageTarget =
  | { _: "item"; itemName: string }
  | { _: "npc"; npcName: string }
  | { _: "quest"; questId: number }
  | { _: "draft"; draftId?: number | null }
  | { _: "wiki"; pageId: number };

type Markdown = { root: Root; dat: MDDat; gver: GameVer };

export function PageEditor(props: {
  authUser?: AuthUser;
  target: MdPageTarget;
  page?: Page;
  breadcrumbList: {
    title: string;
    url: string;
  }[];
}) {
  const t = useT();
  const realm = useRealm();
  const navigate = useNavigate();
  const params = useParams();

  const { authUser, target, page } = props;
  let game: GameVer[] = (page?.game as GameVer[]) ?? [realm.game];
  const showElements = {
    title: true,
    content: true,
    game: true,
    slug: false,
    public: false,
  };
  switch (target._) {
    case "item":
    case "npc":
    case "quest":
      game = [];
      showElements.title = false;
      showElements.game = false;
      break;
    case "draft":
      showElements.public = true;
      game;
      break;
    default:
      game;
      break;
  }
  const [activeLang, setActiveLang] = useState<Lang>("en");
  const [preview, setPreview] = useState<{
    en?: Markdown;
    ru?: Markdown;
  }>(page?.content ?? {});
  const [isMoving, setIsMoving] = useState<boolean>(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [state, setState] = useState({
    titleEn: page?.revision.titleEn ?? "",
    contentEn: page?.revision.contentEn ?? "",
    titleRu: page?.revision.titleRu ?? "",
    contentRu: page?.revision.contentRu ?? "",
    public: page?.draft?.public ?? false,
    deleteDraft: true,
    game,
  });
  const [images, setImages] = useState<
    { id?: number; file?: File; path: string; uploading?: boolean }[]
  >(page?.images ?? []);
  const [revisionImageIds, setRevisionImageIds] = useState<Set<number>>(
    new Set(page?.revision.images?.map((img) => img))
  );
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const breadcrumbList = [...props.breadcrumbList];
  breadcrumbList.pop();

  function insertMdLink(mdLink: string) {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const { selectionStart, selectionEnd, value } = textarea;

    const before = value.substring(0, selectionStart ?? value.length);
    const after = value.substring(selectionEnd ?? value.length);

    const newValue = `${before}${mdLink}${after}`;
    textarea.value = newValue;

    // Программно обновим стейт
    handleChange({
      target: {
        name: activeLang === "en" ? "contentEn" : "contentRu",
        value: newValue,
      },
    } as React.ChangeEvent<HTMLTextAreaElement>);

    // Переместим курсор после вставленного текста
    const pos = before.length + mdLink.length;
    textarea.setSelectionRange(pos, pos);
    textarea.focus();
  }

  function handleChange(
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) {
    const { name, value } = e.target;

    setState((prevState) => ({ ...prevState, [name]: value }));
  }

  function handleCheckboxChange(value: GameVer) {
    setState((prev) => ({
      ...prev,
      game: prev.game.includes(value)
        ? prev.game.filter((g) => g !== value)
        : [...prev.game, value],
    }));
  }

  async function doPreview(content: string, lang: "en" | "ru") {
    const result = await callApi(previewPageApi, {
      input: { content },
      realm,
    });

    if (result.t === "success") {
      setPreview((prev) => ({
        ...prev,
        [lang]: result.data.result.content,
      }));
    }
  }

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      const lang = activeLang;
      const content =
        lang === "en" ? state.contentEn.trim() : state.contentRu.trim();

      doPreview(content, lang);
    }, 500);

    return () => clearTimeout(timeoutRef.current);
  }, [state.contentEn, state.contentRu, activeLang]);

  async function doPublishToWiki() {
    if (target._ !== "draft") {
      return;
    }
    console.log(state, target.draftId);

    const result = await callApi(editPageApi, {
      input: {
        ...state,
        target: { _: "publication_wiki", draftId: target.draftId },
      },
      realm,
    });

    console.log(result);

    switch (result.t) {
      case "success": {
        navigate(
          `/wiki/${generateUrl(result.data.result.id, result.data.result.slug)}`
        );
        break;
      }
      case "error": {
        console.log(result.error);
        break;
      }
    }
  }

  async function doPublish() {
    if (target._ !== "draft") {
      return;
    }
    const result = await callApi(editPageApi, {
      input: {
        ...state,
        target: { _: "publication", draftId: target.draftId },
      },
      realm,
    });

    switch (result.t) {
      case "success": {
        navigate(`/users/${authUser?.id}/pages/${result.data.result.id}`);
        break;
      }
      case "error": {
        console.log(result.error);
        break;
      }
    }
  }

  async function doDelete() {
    if (!page) return;

    if (
      !window.confirm(
        t({
          en: "Are you sure you want to delete this page?",
          ru: "Вы уверены, что хотите удалить эту страницу?",
        })
      )
    ) {
      return;
    }

    const result = await callApi(deletePageApi, {
      input: {
        pageId: page.id,
      },
      realm,
    });

    switch (result.t) {
      case "success":
        navigate(`/users/${authUser?.id}/pages`);
        break;
      case "error":
        console.error(result.error);
        break;
    }
  }

  function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    if (!page) return;

    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newImages = Array.from(files).map((file) => {
      return {
        file,
        path: URL.createObjectURL(file),
        uploading: true,
      };
    });

    setImages((prev) => [...prev, ...newImages]);

    e.target.value = "";

    newImages.forEach(async (img) => {
      const formData = new FormData();
      formData.append("file", img.file);

      const result = await callApi(uploadImageApi, {
        input: {
          pageId: page.id,
        },
        body: formData,
        realm,
      });

      switch (result.t) {
        case "success":
          setImages((prev) =>
            prev.map((i) =>
              i.file === img.file
                ? {
                    id: result.image.id,
                    uploading: false,
                    path: result.image.path,
                  }
                : i
            )
          );
          setRevisionImageIds((prev) => new Set(prev).add(result.image.id));
          break;

        case "error":
          console.error(result.error);
          setImages((prev) => prev.filter((i) => i.file !== img.file));
          break;
      }
    });
  }

  function handleRemoveImage(imageId: number) {
    setImages((prev) =>
      prev.filter((img) => {
        return img.id !== imageId;
      })
    );

    setRevisionImageIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(imageId);
      return newSet;
    });
  }

  async function doCreate() {
    const result = await callApi(editPageApi, {
      input: {
        ...state,
        images: Array.from(revisionImageIds),
        target,
      },
      realm,
    });

    switch (result.t) {
      case "success": {
        const pageId = result.data.result.id;
        navigate(getPathByTarget(target, params, pageId));
        break;
      }
      case "error": {
        console.log(result.error);
        break;
      }
    }
  }

  return (
    <div className={clsx(css.root, css.fullscreen)}>
      <div className={css.header}>
        <div className={css.logo}>
          <Logo />
          <div className={css.logoDesc}>
            Page editor: <Breadcrumb noRoot={true} items={breadcrumbList} />
          </div>
          <div className={css.tabs}>
            <Tab
              onClick={() => setActiveLang("en")}
              active={activeLang === "en"}
            >
              {t({ en: "English", ru: "Английский" })}
            </Tab>
            <Tab
              onClick={() => setActiveLang("ru")}
              active={activeLang === "ru"}
            >
              {t({ en: "Russian", ru: "Русский" })}
            </Tab>
          </div>
        </div>
      </div>

      <div className={clsx(css.columns)}>
        <div className={clsx(css.column)}>
          <div className={clsx("scroll-container")}>
            <form
              className={css.form}
              onSubmit={(e) => {
                e.preventDefault();
                doCreate();
              }}
              autoComplete="off"
            >
              {showElements.title && (
                <TitleInput
                  label={t({ en: "Heading", ru: "Заголовок" })}
                  name={activeLang === "en" ? "titleEn" : "titleRu"}
                  value={activeLang === "en" ? state.titleEn : state.titleRu}
                  onChange={handleChange}
                />
              )}

              <Heading3>{t({ en: "Content", ru: "Контент" })}</Heading3>
              <textarea
                ref={textareaRef}
                className={css.textarea}
                name={activeLang === "en" ? "contentEn" : "contentRu"}
                style={{ paddingTop: 10 }}
                value={activeLang === "en" ? state.contentEn : state.contentRu}
                onChange={handleChange}
              />

              <ImageUploader
                images={images}
                revisionImageIds={revisionImageIds}
                onClick={(link) => insertMdLink(link)}
                onChange={handleImageUpload}
                onRemove={handleRemoveImage}
              />

              {(showElements.game || showElements.slug) && (
                <>
                  <div>
                    <div className={clsx(css.wrap)}>
                      {showElements.slug && (
                        <>
                          <Heading4>{t({ en: "Slug", ru: "Слаг" })}</Heading4>

                          {/* <input
                  className={css.input}
                  type="text"
                  name="slug"
                  value={state.slug}
                  onChange={handleChange}
                />
                <div className={css.slug} style={{ marginTop: 16 }}>
                  {Slug(state.slug)}
                </div> */}
                        </>
                      )}
                    </div>

                    <div className={clsx(css.wrap)}>
                      {showElements.game && (
                        <GverSelector
                          onToggle={(ver) => handleCheckboxChange(ver)}
                          selected={state.game}
                        />
                      )}
                    </div>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
        <div className={clsx(css.column)}>
          <div className={clsx("scroll-container")}>
            <Heading3>
              {t({
                en: "Preview (Autoupdate)",
                ru: "Предпросмотр (Автоматическое обновление)",
              })}
            </Heading3>
            {preview[activeLang] && (
              <MdContent>{preview[activeLang]}</MdContent>
            )}
          </div>
        </div>
      </div>

      <div className={css.footer}>
        <ActionButtons
          isMoving={isMoving}
          setIsMoving={setIsMoving}
          isPublishing={isPublishing}
          setIsPublishing={setIsPublishing}
          onSave={doCreate}
          onPublish={doPublish}
          onPublishToWiki={doPublishToWiki}
          onDelete={doDelete}
          isPublic={state.public}
          isDraft={target._ === "draft" && !!target.draftId}
          toggleDeleteDraft={() =>
            setState((s) => ({ ...s, deleteDraft: !s.deleteDraft }))
          }
          deleteDraft={state.deleteDraft}
        />
      </div>
    </div>
  );
}

function getPathByTarget(
  target: MdPageTarget,
  params: Readonly<Params<string>>,
  pageId: number
) {
  switch (target._) {
    case "quest":
      return `/quests/${params.questId}`;
    case "npc":
      return `/npcs/${params.npcId}`;
    case "item":
      return `/items/${params.itemId}`;
    case "draft":
      return `/users/${params.userId}/pages/${params.pageId ?? pageId}`;
    case "wiki":
      return `/wiki/${params.pageId ?? pageId}`;
  }
}

function Tab(props: {
  active: boolean;
  children: ReactNode;
  onClick?: () => void;
}) {
  return (
    <div
      className={clsx(css.tab, props.active && css.active)}
      onClick={props.onClick}
    >
      {props.children}
    </div>
  );
}

function ActionButtons({
  isMoving,
  setIsMoving,
  isPublishing,
  setIsPublishing,
  onSave,
  onPublish,
  onPublishToWiki,
  onDelete,
  isPublic,
  isDraft,
  toggleDeleteDraft,
  deleteDraft,
}: {
  isMoving: boolean;
  setIsMoving: (v: boolean) => void;
  isPublishing: boolean;
  setIsPublishing: (v: boolean) => void;
  onSave: () => void;
  onPublish: () => void;
  onPublishToWiki: () => void;
  onDelete: () => void;
  isPublic: boolean;
  isDraft: boolean;
  toggleDeleteDraft: () => void;
  deleteDraft: boolean;
}) {
  const t = useT();

  if (isMoving) {
    return (
      <div>
        <div style={{ fontSize: 13 }}>
          <div>
            {t({
              en: "After the wiki is published, the page will be visible to everyone.",
              ru: "После того, как вики-страница будет опубликована, она станет доступна для всех.",
            })}
          </div>
          <CheckBox
            checked={deleteDraft}
            onChange={toggleDeleteDraft}
            label="Delete a draft after publication"
          />
        </div>
        <div style={{ display: "inline-flex", gap: 10, marginTop: 10 }}>
          <Button type="button" onClick={() => setIsMoving(false)}>
            {t({ en: "Back", ru: "Назад" })}
          </Button>
          <Button type="button" color="green" onClick={onPublishToWiki}>
            <span style={{ width: 20, height: 20 }}>
              <BookIcon />
            </span>
            {t({ en: "Publish to Wiki", ru: "Опубликовать в Wiki" })}
          </Button>
        </div>
      </div>
    );
  }

  if (isPublishing) {
    return (
      <div>
        <div style={{ fontSize: 13 }}>
          <div>
            {t({
              en: "Once published, your page will be visible to everyone in your user profile.",
              ru: "После публикации страница станет доступна всем в вашем профиле.",
            })}
          </div>
          <CheckBox
            checked={deleteDraft}
            onChange={toggleDeleteDraft}
            label="Delete a draft after publication"
          />
        </div>
        <div style={{ display: "inline-flex", gap: 10, marginTop: 10 }}>
          <Button type="button" onClick={() => setIsPublishing(false)}>
            {t({ en: "Back", ru: "Назад" })}
          </Button>
          <Button type="button" color="green" onClick={onPublish}>
            {t({ en: "Publish Page", ru: "Опубликовать страницу" })}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: 10,
        }}
      >
        <div style={{ display: "inline-flex", gap: 10 }}>
          <Button type="button" onClick={onSave}>
            <span style={{ width: 20, height: 20 }}>
              <FloppyIcon />
            </span>
            {t({ en: "Save", ru: "Сохранить" })}
          </Button>
          {isDraft && (
            <>
              {!isPublic && (
                <Button onClick={() => setIsPublishing(true)}>
                  <span style={{ width: 20, height: 20 }}>
                    <PageIcon />
                  </span>
                  {t({ en: "Publish Page", ru: "Опубликовать страницу" })}
                </Button>
              )}
              <Button
                type="button"
                color="green"
                onClick={() => setIsMoving(true)}
              >
                <span style={{ width: 20, height: 20 }}>
                  <BookIcon />
                </span>
                {t({ en: "Publish to Wiki", ru: "Опубликовать в Wiki" })}
              </Button>
            </>
          )}
        </div>
        {(isDraft || isPublic) && (
          <Button type="button" color="red" onClick={onDelete}>
            <span style={{ width: 20, height: 20 }}>
              <CloseIcon />
            </span>
            {t({ en: "Delete", ru: "Удалить" })}
          </Button>
        )}
      </div>
    </div>
  );
}
